/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: ProjectPage.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-PROJECTS
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * ProjectPage - Страница портфеля проектов со списком и диалогами CRUD.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-14 - UX: заголовок портфеля, счётчик проектов, skeleton при загрузке.
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракты функций, парные END_METHOD.
 * END_CHANGE_SUMMARY
 */

import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import {
  createProject,
  updateProject,
  deleteProject,
} from '../../api/projectApi';
import { useProjects } from '../../context/ProjectsContext';
import ProjectList from './components/ProjectList';
import CreateProjectDialog from './components/CreateProjectDialog';
import EditProjectDialog from './components/EditProjectDialog';
import DeleteProjectDialog from './components/DeleteProjectDialog';

const displayFont = '"Syne", "Inter", sans-serif';

function ProjectListSkeleton() {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(auto-fill, minmax(280px, 1fr))',
        },
        gap: 2,
      }}
    >
      {[0, 1, 2].map((key) => (
        <Skeleton
          key={key}
          variant="rounded"
          height={148}
          sx={{ borderRadius: 1 }}
        />
      ))}
    </Box>
  );
}

/* START_METHOD_ProjectPage */
/* START_CONTRACT: ProjectPage
 * PURPOSE: Страница проектов: список из контекста, создание, редактирование, удаление.
 * INPUTS: {} (данные из ProjectsContext).
 * OUTPUTS: { JSX.Element - страница с списком и диалогами }.
 * SIDE_EFFECTS: HTTP create/update/deleteProject; refetch контекста.
 * LINKS: M-FE-PROJECTS, M-FE-API
 * END_CONTRACT: ProjectPage
 */
export default function ProjectPage() {
  const { projects, loading, error, refetch } = useProjects();

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingProject, setDeletingProject] = useState(null);

  /* START_METHOD_handleCreate */
  /* START_CONTRACT: handleCreate
   * PURPOSE: Создать проект и обновить список.
   * INPUTS: { payload: object }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP createProject; refetch.
   * LINKS: M-FE-PROJECTS
   * END_CONTRACT: handleCreate
   */
  const handleCreate = async (payload) => {
    await createProject(payload);
    await refetch();
  };
  /* END_METHOD_handleCreate */

  /* START_METHOD_handleUpdate */
  /* START_CONTRACT: handleUpdate
   * PURPOSE: Обновить проект по id и обновить список.
   * INPUTS: { id: number, payload: object }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP updateProject; refetch.
   * LINKS: M-FE-PROJECTS
   * END_CONTRACT: handleUpdate
   */
  const handleUpdate = async (id, payload) => {
    await updateProject(id, payload);
    await refetch();
  };
  /* END_METHOD_handleUpdate */

  /* START_METHOD_handleDelete */
  /* START_CONTRACT: handleDelete
   * PURPOSE: Удалить проект по id и обновить список.
   * INPUTS: { id: number }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP deleteProject; refetch.
   * LINKS: M-FE-PROJECTS
   * END_CONTRACT: handleDelete
   */
  const handleDelete = async (id) => {
    await deleteProject(id);
    await refetch();
  };
  /* END_METHOD_handleDelete */

  /* START_METHOD_handleEdit */
  /* START_CONTRACT: handleEdit
   * PURPOSE: Открыть диалог редактирования выбранного проекта.
   * INPUTS: { project: Project }.
   * OUTPUTS: { void }.
   * SIDE_EFFECTS: setEditingProject/setEditOpen.
   * LINKS: M-FE-PROJECTS
   * END_CONTRACT: handleEdit
   */
  const handleEdit = (project) => {
    setEditingProject(project);
    setEditOpen(true);
  };
  /* END_METHOD_handleEdit */

  /* START_METHOD_handleDeleteClick */
  /* START_CONTRACT: handleDeleteClick
   * PURPOSE: Открыть диалог удаления выбранного проекта.
   * INPUTS: { project: Project }.
   * OUTPUTS: { void }.
   * SIDE_EFFECTS: setDeletingProject/setDeleteOpen.
   * LINKS: M-FE-PROJECTS
   * END_CONTRACT: handleDeleteClick
   */
  const handleDeleteClick = (project) => {
    setDeletingProject(project);
    setDeleteOpen(true);
  };
  /* END_METHOD_handleDeleteClick */

  const projectCount = projects.length;
  const projectCountLabel = (() => {
    const mod10 = projectCount % 10;
    const mod100 = projectCount % 100;
    if (mod10 === 1 && mod100 !== 11) return 'проект';
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'проекта';
    return 'проектов';
  })();

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'flex-end' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
          pb: 3,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              color: 'primary.main',
              fontWeight: 600,
              letterSpacing: '0.12em',
              lineHeight: 1.4,
              mb: 0.5,
            }}
          >
            Портфель
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontFamily: displayFont,
                fontWeight: 700,
                letterSpacing: '-0.03em',
                lineHeight: 1.15,
              }}
            >
              Проекты
            </Typography>
            {!loading && projectCount > 0 && (
              <Chip
                label={`${projectCount} ${projectCountLabel}`}
                size="small"
                sx={{
                  fontWeight: 600,
                  bgcolor: (theme) => theme.palette.primary.main + '14',
                  color: 'primary.main',
                  border: 'none',
                }}
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 480 }}>
            {loading
              ? 'Загружаем список…'
              : projectCount > 0
                ? 'Выберите проект, чтобы перейти к задачам, ролям и составу команды.'
                : 'Здесь собираются все команды и их задачи.'}
          </Typography>
        </Box>

        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, flexShrink: 0 }}
        >
          Новый проект
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <ProjectListSkeleton />
      ) : (
        <ProjectList
          projects={projects}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onCreate={() => setCreateOpen(true)}
        />
      )}

      <CreateProjectDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
      />

      <EditProjectDialog
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditingProject(null);
        }}
        onUpdate={handleUpdate}
        project={editingProject}
      />

      <DeleteProjectDialog
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setDeletingProject(null);
        }}
        onConfirm={handleDelete}
        project={deletingProject}
      />
    </Box>
  );
}
/* END_METHOD_ProjectPage */
